import { useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { BuildingModel, CommonFacilityModel, CommonFacilityType } from "src/api/building/building-types";
import { BuildingFacilityTagModel } from "src/api/building/commonfacility-types";
import { Modal } from "src/api/public-types";
import { BaseButton, BaseModal, BaseToggle, Tab } from "src/components";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { useQueryParams } from "src/hooks/useQueryParams";
import { facilitySubTabs, sortingBuildingFacilities } from "src/pages/building/building-types";
import ViewDataTable from "src/pages/building/components/ViewDataTable";
import useFacilityV2, { mapBuildingFacilityToCommon } from "src/pages/building/hooks/useFacilityV2";
import { useModal } from "src/recoil/modalState/hook";
import { globalPartnerState } from "src/recoil/partners/atom";
import { PaPartner } from "src/types/partner";
import { meetingRoomColumns, refreshRoomColumns } from "../columns/BuildingFacilityColumns";
import CommonSpaceFormModal from "./CommonSpaceFormModal";

interface CommonFacilityWithTag extends CommonFacilityModel {
  facilityTagList: BuildingFacilityTagModel[];
}

type Props = {
  building: BuildingModel;
  fetchBuilding: (buildingId: number) => void;
};

/*
  회의실/편의시설 컴포넌트 (RPBF01~03/07 기반)
 */
const CommonSpaceSection = ({ building }: Props) => {
  // 건물 공용공간의 타입
  const {
    queryParams: { facilityType: facilityTypeParam },
  } = useQueryParams();
  const facilityType = facilityTypeParam as CommonFacilityType;

  const [isUsedFacility, setIsUsedFacility] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>(facilitySubTabs[0]);
  const [facilityList, setFacilityList] = useState<CommonFacilityModel[]>([]);
  const partner = useRecoilValue(globalPartnerState);

  const isMeetingRoom = useMemo(() => {
    return facilityType === "MEETING_ROOM";
  }, [facilityType]);

  // 출입그룹 기능 활성 권한 여부
  const isAccessFeature = partner?.features?.some((feature: PaPartner) => feature.name === "FEATURE_RESERVATION_ACCESS" && feature.active) || false;

  const { isAuthority, fetchAuthorityReadonly } = usePartnerAuthority();

  // 공용공간 RPBF API
  const { fetchFacilities, saveFacility, removeFacility } = useFacilityV2();

  // 회의실/편의시설 목록 조회 (RPBF01)
  const getBuildingFacilityList = async () => {
    if (!building?.id) return;
    const list = await fetchFacilities(facilityType, building.id);
    setFacilityList(sortingBuildingFacilities(list));
  };

  // building.id / facilityType 변경 시에만 재조회 (fetchFacilities 는 매 렌더 새로 생성되므로 deps 에서 제외 → 무한 렌더 방지)
  useEffect(() => {
    getBuildingFacilityList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [building?.id, facilityType]);

  // 모달 관리
  const { setAbstractModalZ1 } = useModal();

  // 저장 모달 관리
  const [confirmModal, setConfirmModal] = useState<Modal>({ isOpen: false });
  const [openFormModal, setOpenFormModal] = useState<Modal & { readonly?: boolean }>({ isOpen: false });

  const [newFacility, setNewFacility] = useState<CommonFacilityModel>();

  useEffect(() => {
    setIsUsedFacility(facilityList.length > 0 ? true : false);
  }, [facilityList]);

  const handleResetStates = () => {
    setNewFacility(undefined);
    setActiveTab(facilitySubTabs[0]);
  };

  const openFacilityModal = (facility: { data: CommonFacilityModel; readonly: boolean }) => {
    const { data, readonly = false } = facility;
    setOpenFormModal({ isOpen: true, payload: data, readonly: readonly });
    handleResetStates();
  };

  // 삭제 (RPBF07) - id 가 있으면 단건, 없으면 전체 초기화
  const handleRemoveMeetingRoom = async (id?: number) => {
    if (id) {
      await removeFacility(building.id, id);
      await getBuildingFacilityList();
      setConfirmModal({ isOpen: false });
      setOpenFormModal({ isOpen: false });
      return;
    }
    // 전체 초기화 (사용 토글 OFF): 현재 목록 전부 삭제
    await Promise.all(
      facilityList.filter((facility) => facility.id).map((facility) => removeFacility(building.id, Number(facility.id)).catch(() => undefined)),
    );
    await getBuildingFacilityList();
    setConfirmModal({ isOpen: false });
    setOpenFormModal({ isOpen: false });
  };

  // 회의실/편의시설 저장 (RPBF02 등록 / RPBF03 수정)
  const onSaveFacility = async (passData: CommonFacilityWithTag) => {
    const isEdit = !!passData.id;
    const { data } = await saveFacility(facilityType, building.id, passData);
    setConfirmModal({ isOpen: false });
    await getBuildingFacilityList();

    const savedFacility = data?.data?.facility;

    // 신규 등록 + 출입그룹 기능 활성 → 출입그룹 설정 이동 유도
    if (!isEdit && isAccessFeature && savedFacility) {
      setNewFacility(mapBuildingFacilityToCommon(savedFacility, facilityType));
      setConfirmModal({
        isOpen: true,
        title: "저장이 완료되었습니다",
        message: "이어서 출입그룹 설정을 할 수 있습니다.\n 이동하시겠습니까?",
        type: "MOVED_ACCESS_GROUP",
      });
    } else {
      setOpenFormModal({ isOpen: false });
    }
  };

  const handleConfirm = async (passData?: CommonFacilityModel | { data: CommonFacilityModel; facilityTagList: BuildingFacilityTagModel[] }) => {
    if (!passData) {
      setConfirmModal({ isOpen: true, title: "정보가 초기화되어 저장됩니다.", type: "FACILITY_DELETE" });
    } else {
      setConfirmModal({
        isOpen: true,
        title: "저장하시겠습니까?",
        payload: passData,
      });
    }
  };

  // 저장 모달 확인 클릭시 실행
  const executeConfirm = async () => {
    // 삭제
    if (confirmModal.type === "FACILITY_DELETE") {
      const id = confirmModal.payload as number;
      id ? handleRemoveMeetingRoom(id) : handleRemoveMeetingRoom();
    }
    // 출입그룹 이동
    else if (confirmModal.type === "MOVED_ACCESS_GROUP") {
      setActiveTab(facilitySubTabs[1]);
      setConfirmModal({ isOpen: false });
    }
    // 저장
    else {
      onSaveFacility(confirmModal.payload as CommonFacilityWithTag);
    }
  };

  // 태그 목록 모달 오픈
  const handleOpenTagListModal = () => {
    setAbstractModalZ1({
      isOpen: true,
      size: "large",
    });
  };

  return (
    <section className="">
      <div className="contents-container__sub-title">
        <div className="flex-center">
          <h2 className="minmax80">설정</h2>
          <BaseToggle
            checked={isUsedFacility}
            onChange={(checked: boolean) => {
              if (!isAuthority("w")) {
                return fetchAuthorityReadonly(true);
              }
              if (facilityList.length > 0 && !checked) {
                handleConfirm();
              } else {
                setIsUsedFacility(checked);
              }
            }}
          />
        </div>
      </div>
      {isUsedFacility && (
        <>
          <div className="flex-center-between mb30" style={{ minHeight: 40 }}>
            <h2>{isMeetingRoom ? "회의실 목록" : "편의시설 목록"}</h2>
            <div className="flex-center ">
              <BaseButton
                title="태그관리"
                className="color-white mr10"
                onClick={() => {
                  handleOpenTagListModal();
                }}
              />
              {isAuthority("w") && (
                <BaseButton
                  title={isMeetingRoom ? "회의실 등록" : "편의시설 등록"}
                  onClick={() => {
                    handleResetStates();
                    setOpenFormModal({ isOpen: true });
                  }}
                />
              )}
            </div>
          </div>

          <ViewDataTable
            columns={(isMeetingRoom ? meetingRoomColumns : refreshRoomColumns).filter((column) => {
              if (!isAuthority("w") && column.accessor === "edit") return false;
              if (!isAuthority("d") && column.accessor === "delete") return false;
              return true;
            })}
            data={facilityList}
            tableFunction={{
              openFacilityModal,
              setConfirmModal,
            }}
            isShowScrollShadow
          />
        </>
      )}
      {confirmModal.isOpen && (
        <BaseModal
          isOpen={true}
          title={confirmModal.title}
          onClick={() => {
            executeConfirm();
          }}
          onClose={() => {
            if (confirmModal.type === "MOVED_ACCESS_GROUP") {
              handleResetStates();
              setOpenFormModal({ isOpen: false });
            }
            setConfirmModal({ isOpen: false });
          }}
          btnLeftTitle="취소"
          btnRightTitle={confirmModal.type === "MOVED_ACCESS_GROUP" ? "확인" : "저장"}
        >
          {confirmModal.message && <p>{confirmModal.message}</p>}
        </BaseModal>
      )}

      {openFormModal.isOpen && (
        <CommonSpaceFormModal
          title={isMeetingRoom ? "회의실" : "편의시설"}
          onClose={() => setOpenFormModal({ isOpen: false })}
          handleConfirm={handleConfirm}
          facility={newFacility ? newFacility : openFormModal.payload}
          readonly={openFormModal.readonly}
          building={building}
          activeTab={activeTab}
          onChangeTab={setActiveTab}
          isAccessFeature={isAccessFeature}
        />
      )}
    </section>
  );
};

export default CommonSpaceSection;
